<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Info, AlertTriangle, CalendarCheck } from 'lucide-vue-next'

interface TimeSlot {
  day: string
  time: string
  label: string
  is_available?: boolean
  slotTag?: string
  subjectId?: number
  subjectName?: string
  levelId?: number
  levelName?: string
}

interface GroupOption {
  id: string
  subject_id: number
  level_id: number
  label: string
  slots: TimeSlot[]
}

interface Props {
  modelValue: {
    day: string
    time: string
    slotTag?: string
    subjectId?: number
    subjectName?: string
    levelId?: number
    levelName?: string
  }
  tierSlug: 'silver' | 'gold'
  groupAvailability?: GroupOption[]
  oneToOneAvailability?: TimeSlot[]
  validationWindowHours?: number
}

const props = defineProps<Props>()

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * The next time the chosen weekday and time comes round, strictly in the future.
 */
function nextOccurrence(day: string, time: string, from: Date): Date | null {
  const targetDay = DAYS.findIndex(d => d.toLowerCase() === day.toLowerCase())
  if (targetDay === -1) return null

  const [hour, minute] = time.split(':').map(Number)
  if (Number.isNaN(hour)) return null

  const occurrence = new Date(from)
  occurrence.setHours(hour, minute || 0, 0, 0)

  let ahead = (targetDay - from.getDay() + 7) % 7
  // Today's slot only counts if its time has not already passed.
  if (ahead === 0 && occurrence <= from) ahead = 7
  occurrence.setDate(occurrence.getDate() + ahead)

  return occurrence
}

/**
 * A tutor may take the full confirmation window to respond. If the chosen slot
 * comes round before that window closes, this week's session is not guaranteed
 * and the first lesson could be the week after. Say so before they pay.
 */
const firstLesson = computed(() => {
  const { day, time } = props.modelValue
  const windowHours = props.validationWindowHours

  if (!day || !time || !windowHours) return null

  const now = new Date()
  const occurrence = nextOccurrence(day, time, now)
  if (!occurrence) return null

  const deadline = new Date(now.getTime() + windowHours * 3600 * 1000)
  const atRisk = occurrence < deadline

  const guaranteed = new Date(occurrence)
  if (atRisk) guaranteed.setDate(guaranteed.getDate() + 7)

  const fmt = (d: Date) => d.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  })
  const fmtWithTime = (d: Date) => `${fmt(d)} at ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit',
  })}`

  return {
    atRisk,
    deadline: fmtWithTime(deadline),
    thisWeek: fmt(occurrence),
    guaranteed: fmt(guaranteed),
  }
})
const emit = defineEmits<{
  'update:modelValue': [value: { day: string; time: string; slotTag?: string; subjectId?: number; subjectName?: string; levelId?: number; levelName?: string }]
}>()

// For silver tier: selected course (subject+level combo)
const selectedCourseId = ref<string | null>(null)

// Get the selected course option
const selectedCourse = computed(() => {
  if (!selectedCourseId.value || !props.groupAvailability) return null
  return props.groupAvailability.find(c => c.id === selectedCourseId.value) || null
})

// Check if group availability has any options at all
const hasGroupAvailability = computed(() => {
  return (props.groupAvailability?.length ?? 0) > 0
})

// Get available time slots based on tier, filtering out full slots
const availableSlots = computed(() => {
  let slots: TimeSlot[] = []
  if (props.tierSlug === 'gold') {
    slots = props.oneToOneAvailability || []
  } else {
    slots = selectedCourse.value?.slots || []
  }
  return slots.filter(s => s.is_available !== false)
})

// Update handlers
function updateCourse(event: Event) {
  const target = event.target as HTMLSelectElement
  selectedCourseId.value = target.value || null

  const course = props.groupAvailability?.find(c => c.id === target.value)
  emit('update:modelValue', {
    day: '',
    time: '',
    subjectId: course?.subject_id,
    levelId: course?.level_id,
  })
}

function updateTimeSlot(event: Event) {
  const target = event.target as HTMLSelectElement
  const slot = availableSlots.value.find(s => `${s.day}-${s.time}` === target.value)

  if (slot) {
    emit('update:modelValue', {
      ...props.modelValue,
      day: slot.day,
      time: slot.time,
      slotTag: slot.slotTag,
      subjectId: slot.subjectId ?? props.modelValue.subjectId,
      subjectName: slot.subjectName ?? props.modelValue.subjectName,
      levelId: slot.levelId ?? props.modelValue.levelId,
      levelName: slot.levelName ?? props.modelValue.levelName,
    })
  } else {
    emit('update:modelValue', {
      ...props.modelValue,
      day: '',
      time: '',
      slotTag: undefined,
    })
  }
}

// Current selected slot value for the dropdown
const selectedSlotValue = computed(() => {
  if (props.modelValue.day && props.modelValue.time) {
    return `${props.modelValue.day}-${props.modelValue.time}`
  }
  return ''
})

// Sync selectedCourseId with modelValue on mount
watch(() => props.modelValue, (newValue) => {
  if (newValue.subjectId && newValue.levelId && props.groupAvailability) {
    const courseId = `${newValue.subjectId}-${newValue.levelId}`
    if (props.groupAvailability.some(c => c.id === courseId)) {
      selectedCourseId.value = courseId
    }
  }
}, { immediate: true })
</script>

<template>
  <section class="bg-white rounded-2xl shadow-sm p-4 md:p-8">
    <h2 class="text-xl font-semibold text-gray-900 mb-6">
      {{ tierSlug === 'gold' ? 'Select Your 1:1 Session Time' : 'Select Your Group Session' }}
    </h2>

    <div class="space-y-6">
      <!-- No availability at all (Silver with no group options, or Gold with no 1:1 slots) -->
      <div
        v-if="(tierSlug === 'silver' && !hasGroupAvailability) || (tierSlug === 'gold' && availableSlots.length === 0)"
        class="text-center py-6 bg-gray-50 rounded-xl"
      >
        <p class="text-gray-500">No available time slots at the moment.</p>
        <p class="text-sm text-gray-400 mt-1">Please contact the tutor for availability.</p>
      </div>

      <template v-else>
        <!-- Course Selection (Silver tier only) -->
        <div v-if="tierSlug === 'silver'">
          <label class="block text-sm font-semibold text-gray-700 mb-2">Select Subject & Level</label>
          <select
            :value="selectedCourseId || ''"
            @change="updateCourse"
            class="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-900"
          >
            <option value="">Choose a subject & level</option>
            <option
              v-for="course in groupAvailability"
              :key="course.id"
              :value="course.id"
            >
              {{ course.label }}
            </option>
          </select>
        </div>

        <!-- Time Slot Selection -->
        <div v-if="tierSlug === 'gold' || selectedCourseId">
          <div v-if="availableSlots.length === 0" class="text-center py-6 bg-gray-50 rounded-xl">
            <p class="text-gray-500">No available time slots at the moment.</p>
            <p class="text-sm text-gray-400 mt-1">Please contact the tutor for availability.</p>
          </div>

          <div v-else>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Select Time Slot</label>
            <select
              :value="selectedSlotValue"
              @change="updateTimeSlot"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-900"
            >
              <option value="">Choose a time slot</option>
              <option
                v-for="slot in availableSlots"
                :key="`${slot.day}-${slot.time}`"
                :value="`${slot.day}-${slot.time}`"
              >
                {{ slot.label }}
              </option>
            </select>
          </div>
        </div>
      </template>

      <!-- First lesson expectation, once a slot is chosen -->
      <div
        v-if="firstLesson"
        :class="[
          'rounded-xl px-4 py-3 text-sm',
          firstLesson.atRisk ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200',
        ]"
      >
        <p v-if="firstLesson.atRisk" class="flex items-start text-amber-900">
          <AlertTriangle class="w-4 h-4 mr-2 mt-0.5 shrink-0 text-amber-600" />
          <span>
            Your tutor has until <strong>{{ firstLesson.deadline }}</strong> to confirm your request.
            If they confirm after {{ firstLesson.thisWeek }}, your first lesson will be
            <strong>{{ firstLesson.guaranteed }}</strong> instead.
          </span>
        </p>
        <p v-else class="flex items-start text-emerald-900">
          <CalendarCheck class="w-4 h-4 mr-2 mt-0.5 shrink-0 text-emerald-600" />
          <span>
            Once your tutor confirms, your first lesson will be <strong>{{ firstLesson.guaranteed }}</strong>.
          </span>
        </p>
      </div>

      <!-- Info text -->
      <p class="text-xs text-gray-500 flex items-start">
        <Info class="w-4 h-4 mr-1.5 mt-0.5 shrink-0" />
        <span v-if="tierSlug === 'gold'">
          Select your preferred time for your weekly 1:1 session. You can change this anytime from your dashboard.
        </span>
        <span v-else>
          Select your subject, level, and preferred time for your weekly group session. You can change this anytime from your dashboard.
        </span>
      </p>
    </div>
  </section>
</template>
